import {
  AdminBootstrapResponse,
  AdminCatalogs,
  AdminCatalogSnapshot,
  AdminPrograma,
  AdminAgencia, AdminSubagencia, AdminZona, AgencyMutationResponse, SubagencyMutationResponse, ZoneMutationResponse,
  CatalogRecord,
  ProgramMutationResponse
} from '../../models/api/admin-catalogs';
import { ProductTypeMutationResponse, ProductTypeRecord } from '../../models/api/admin-catalogs';
import { PackageTypeMutationResponse, PackageTypeRecord } from '../../models/api/admin-catalogs';
import { ApiClientError } from '../../models/api/api-envelope';

const CATALOG_KEYS: Array<keyof AdminCatalogs> = [
  'usuarios', 'tiposProducto', 'tiposPaquete', 'productos', 'paquetes',
  'paqueteProductos', 'eventos', 'zonas', 'diccionario', 'programas', 'agencias', 'subagencias',
  'tiposUsuario', 'usuariosPuntoVentaModulos', 'tiposUsuarioPuntoVentaModulos', 'monedas', 'tiposCambio'
];

function invalid(): ApiClientError {
  return new ApiClientError('invalid-response', 'El catálogo administrativo no es válido.');
}

export function normalizeCatalogId(value: any): number {
  if (typeof value === 'number') {
    if (Number.isSafeInteger(value) && value >= 0) {
      return value;
    }
    throw invalid();
  }
  if (typeof value === 'string' && /^(0|[1-9][0-9]*)$/.test(value)) {
    const normalized = Number(value);
    if (Number.isSafeInteger(normalized)) {
      return normalized;
    }
  }
  throw invalid();
}

function isIdField(name: string): boolean {
  return /^id(?:[a-z_]|$)/i.test(name);
}

function normalizeRecord(value: any, rejectSecrets: boolean): CatalogRecord {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw invalid();
  }
  const normalized: CatalogRecord = {};
  Object.keys(value).forEach(function (name) {
    if (rejectSecrets && /^(password|apikey)$/i.test(name)) {
      throw invalid();
    }
    if (isIdField(name)) {
      normalized[name] = normalizeCatalogId(value[name]);
    } else if (Array.isArray(value[name])) {
      normalized[name] = value[name].map(function (item) {
        return item && typeof item === 'object' ? normalizeRecord(item, rejectSecrets) : item;
      });
    } else if (value[name] && typeof value[name] === 'object') {
      normalized[name] = normalizeRecord(value[name], rejectSecrets);
    } else {
      normalized[name] = value[name];
    }
  });
  return normalized;
}

function normalizeCatalog(value: any, rejectSecrets: boolean): CatalogRecord[] {
  if (!Array.isArray(value)) {
    throw invalid();
  }
  return value.map(function (record) {
    return normalizeRecord(record, rejectSecrets);
  });
}

export function mapAdminBootstrapResponse(response: AdminBootstrapResponse,
  requestedLocationId: number | string): AdminCatalogSnapshot {
  if (!response || response.code !== '0' || !response.data || response.data.snapshot !== true) {
    throw invalid();
  }
  const data: any = response.data;
  const schemaVersion = normalizeCatalogId(data.schemaVersion);
  const selectedLocationId = normalizeCatalogId(data.selectedLocationId);
  const requested = normalizeCatalogId(requestedLocationId);
  if (schemaVersion !== 1 || selectedLocationId !== requested ||
    typeof data.generatedAt !== 'string' || data.generatedAt.length === 0) {
    throw invalid();
  }
  const selectedLocation: any = normalizeRecord(data.selectedLocation, false);
  if (selectedLocation.idlocacion !== selectedLocationId || !data.catalogs ||
    typeof data.catalogs !== 'object' || Array.isArray(data.catalogs)) {
    throw invalid();
  }

  const catalogs: any = {};
  CATALOG_KEYS.forEach(function (key) {
    catalogs[key] = normalizeCatalog(data.catalogs[key], key === 'usuarios');
  });
  catalogs.agencias = mapAgencies(catalogs.agencias, selectedLocationId);
  catalogs.subagencias = mapSubagencies(catalogs.subagencias, catalogs.agencias, selectedLocationId);
  catalogs.zonas = mapZones(catalogs.zonas, selectedLocationId);

  return {
    schemaVersion: schemaVersion,
    generatedAt: data.generatedAt,
    snapshot: true,
    selectedLocationId: selectedLocationId,
    selectedLocation: selectedLocation,
    catalogs: catalogs
  };
}

function requiredName(record: CatalogRecord, field: string): string {
  const name = typeof record[field] === 'string' ? String(record[field]).trim() : '';
  if (!name || name.length > 200) { throw invalid(); }
  return name;
}

function mapAgencies(records: CatalogRecord[], locationId: number): AdminAgencia[] {
  return records.map(function (record) {
    if (record.idlocacion !== locationId || record.idagencia === undefined) { throw invalid(); }
    return { idlocacion: record.idlocacion, idagencia: record.idagencia, agencia: requiredName(record, 'agencia') };
  });
}

function mapSubagencies(records: CatalogRecord[], agencies: AdminAgencia[], locationId: number): AdminSubagencia[] {
  return records.filter(function (record) {
    return record.idlocacion === locationId && agencies.some(function (agency) {
      return agency.idagencia === record.idagencia && agency.idlocacion === record.idlocacion;
    });
  }).map(function (record) {
    if (record.idsubagencia === undefined) { throw invalid(); }
    return { idlocacion: record.idlocacion, idagencia: record.idagencia,
      idsubagencia: record.idsubagencia, subagencia: requiredName(record, 'subagencia') };
  });
}

function mapZones(records: CatalogRecord[], locationId: number): AdminZona[] {
  return records.map(function (record) {
    if (record.idlocacion !== locationId || record.idzona === undefined ||
      (record.idestatus !== 1 && record.idestatus !== 2)) { throw invalid(); }
    return { idlocacion: record.idlocacion, idzona: record.idzona,
      zona: requiredName(record, 'zona'), idestatus: record.idestatus };
  });
}

export function mapAgencyMutationResponse(response: AgencyMutationResponse,
  locationId: number | string): AdminAgencia {
  const record: any = mutationRecord(response, locationId);
  if (record.idagencia === undefined) { throw invalid(); }
  return { idlocacion: record.idlocacion, idagencia: record.idagencia, agencia: requiredName(record, 'agencia') };
}

export function mapSubagencyMutationResponse(response: SubagencyMutationResponse,
  locationId: number | string, agencyId: number | string): AdminSubagencia {
  const record: any = mutationRecord(response, locationId);
  if (record.idagencia !== normalizeCatalogId(agencyId) || record.idsubagencia === undefined) { throw invalid(); }
  return { idlocacion: record.idlocacion, idagencia: record.idagencia,
    idsubagencia: record.idsubagencia, subagencia: requiredName(record, 'subagencia') };
}

export function mapZoneMutationResponse(response: ZoneMutationResponse,
  locationId: number | string): AdminZona {
  const record: any = mutationRecord(response, locationId);
  if (record.idzona === undefined || (record.idestatus !== 1 && record.idestatus !== 2)) { throw invalid(); }
  return { idlocacion: record.idlocacion, idzona: record.idzona,
    zona: requiredName(record, 'zona'), idestatus: record.idestatus };
}

function mutationRecord(response: any, locationId: number | string): CatalogRecord {
  if (!response || response.code !== '0' || !response.data) { throw invalid(); }
  const record = normalizeRecord(response.data, false);
  if (record.idlocacion !== normalizeCatalogId(locationId)) { throw invalid(); }
  return record;
}

export function mapProductTypeMutationResponse(response: ProductTypeMutationResponse,
  requestedLocationId: number | string): ProductTypeRecord {
  if (!response || response.code !== '0' || !response.data) {
    throw invalid();
  }
  const record: any = normalizeRecord(response.data, false);
  const name = typeof record.tipoproducto === 'string' ? record.tipoproducto.trim() : '';
  if (!name || name.length > 200 || record.idlocacion === undefined ||
    record.idtipoproducto === undefined) {
    throw invalid();
  }
  if (record.idlocacion !== normalizeCatalogId(requestedLocationId)) {
    throw invalid();
  }
  record.tipoproducto = name;
  return record as ProductTypeRecord;
}

export function mapPackageTypeMutationResponse(response: PackageTypeMutationResponse,
  requestedLocationId: number | string): PackageTypeRecord {
  if (!response || response.code !== '0' || !response.data) {
    throw invalid();
  }
  const record: any = normalizeRecord(response.data, false);
  const name = typeof record.tipopaquete === 'string' ? record.tipopaquete.trim() : '';
  if (!name || name.length > 200 || record.idlocacion === undefined ||
    record.idtipopaquete === undefined || (record.idestatus !== 1 && record.idestatus !== 2)) {
    throw invalid();
  }
  if (record.idlocacion !== normalizeCatalogId(requestedLocationId)) {
    throw invalid();
  }
  record.tipopaquete = name;
  return record as PackageTypeRecord;
}

export function mapProgramMutationResponse(response: ProgramMutationResponse,
  requestedLocationId: number | string): AdminPrograma {
  if (!response || response.code !== '0' || !response.data) {
    throw invalid();
  }
  const record = normalizeRecord(response.data, false);
  const name = typeof record.programa === 'string' ? record.programa.trim() : '';
  if (!name || name.length > 200 || record.idlocacion === undefined ||
    record.idprograma === undefined || (record.idestatus !== 1 && record.idestatus !== 2)) {
    throw invalid();
  }
  if (record.idlocacion !== normalizeCatalogId(requestedLocationId)) {
    throw invalid();
  }
  return {
    idlocacion: record.idlocacion,
    idprograma: record.idprograma,
    programa: name,
    idestatus: record.idestatus
  };
}



// WEBPACK FOOTER //
// ./src/providers/catalogs/catalogs-mapper.ts